"use client";

import { useEffect } from "react";

/**
 * Root error boundary. Catches a thrown error anywhere under the root layout
 * (every page except the layout itself — see global-error.tsx's doc-comment)
 * and beacons it to /api/client-error so a client-side crash in a revenue
 * path (checkout, dashboard, the apply flow) has server-side visibility
 * instead of silently falling through to Next's bare default error UI.
 * Ported from talentists-tutor's TT-195.
 */
export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    fetch("/api/client-error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        message: error.message,
        digest: error.digest,
        stack: error.stack,
        path: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  }, [error]);

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-20 text-center sm:px-6">
      <p className="telex text-[11px] uppercase tracking-wide text-neutral-400">
        Finseats
      </p>
      <h1 className="display mt-3 text-[1.75rem] font-semibold text-ink sm:text-3xl">
        Something went wrong
      </h1>
      <p className="mt-3 max-w-md text-[15px] leading-relaxed text-neutral-600">
        This page hit an unexpected error. Reloading usually fixes it — if it
        keeps happening, let us know what you were doing.
      </p>
      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => reset()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex h-11 items-center justify-center rounded-md border border-neutral-200 px-5 text-sm font-semibold text-neutral-700 transition-colors hover:border-neutral-400 hover:text-ink"
        >
          Reload page
        </button>
      </div>
    </main>
  );
}
